import React, { useEffect }                from "react";
import { useTranslation }                  from "react-i18next";
import { ThemeProvider as ThemeContainer } from "react-jss";
import { ThemeProvider }                   from "@mui/material/styles";
import CssBaseline                         from "@mui/material/CssBaseline";
import { ToastContainer }                  from "react-toastify";
import { CacheProvider }                   from "@emotion/react";
import Script                              from "next/script";

import { useGetLoggedIn, useSetProfileData, useSetLoggedIn }             from "_common/hooks/auth";
import { useGetAppLocale }                                               from "_common/hooks/global";
import Analytics                                                         from "_analytics/index";
import RouterTransition                                                  from "_components/atoms/routerTransition";
import Toast                                                             from "_common/toast";
import { removeJSSLogs }                                                 from "_common/utils";
import { storeAuthDataClient }                                           from "_common/utils/user";
import { logoutUser }                                                    from "_components/pages/auth/apis";
import withAuth                                                          from "_components/templates/authentication";
import Layout                                                            from "_components/templates/layout";
import createEmotionCache                                                from "_styles/createEmotionCache";
import { useGetDarkMode }                                                from "_common/hooks/global";
import { cssVariables, themeLight, light, dark, palette, typographyNew } from "_styles/theme";
import muiTheme                                                          from "_styles/theme/mui";
import Schema                                                            from "_common/schema";
import { initSingular }                                                  from "_root/analytics/index";
import { GoogleOAuthProvider }                                           from "@react-oauth/google";
import "_common/translation/i18n";

// ---- Import styles here ----
import "react-toastify/dist/ReactToastify.min.css";
import "_styles/common/vjs-live-component.css"; /* Please do not delete this styles file, some components require a separate css from this file */
import "_styles/common/ivsPlayer/index.css";
import "_styles/pages/download/index.css";
import "_styles/common/style.css";
import "_styles/common/player.css";
import "_styles/common/globals.css";

// ---- Import Capture session here (Avoid API calls as much as possible in app.js ) ---
import { captureUserVisitingSession } from "_services/api/user";

// ---- Import styles here ----
const clientSideEmotionCache = createEmotionCache ();

const component = props => {
	const { Component, pageProps, emotionCache = clientSideEmotionCache } = props;

	if ( pageProps.customPage )
		return (
			<Component
				{ ...props }
				{ ...pageProps }
			/>
		);

	const darkModeCookie = pageProps?.theme?.darkMode === undefined ? false : pageProps?.theme?.darkMode;
	const darkMode       = !process.browser ? darkModeCookie : useGetDarkMode ();
	const setLoggedIn    = useSetLoggedIn ();
	const setProfile     = useSetProfileData ();
	const { i18n }       = useTranslation ();
	const appLocale      = useGetAppLocale ();
	const isLoggedIn     = useGetLoggedIn ();

	removeJSSLogs ();

	useEffect ( () => {
		if ( "serviceWorker" in navigator ) {
			navigator.serviceWorker.register ( "/sw1.js" ).then (
				res => {
					return res;
				}, () => {}
			);
		}
		initSingular ();
	}, [] );

	useEffect ( () => {
		if ( !isLoggedIn )
			return;

		/*
            This is for capturing user visiting session,
            capture sessions are incremented on every login
            (in case of android/iOS it is on splash screens).

            We have to capture sessions for non-duplicate API responses(Eg. Notifications)
        */
		try {
			captureUserVisitingSession ();
		} catch ( e ) {
			return;
		}

	}, [ isLoggedIn ] );

	useEffect ( () => {
		if ( i18n.language !== appLocale )
			i18n.changeLanguage ( appLocale );

	}, [ appLocale ] );

	const resetUser = async () => {

		await logoutUser ( { forceLogout: true } );

		setProfile ( undefined );
		setLoggedIn ( false );

		Toast.warn ( "Session expired. Please reload the page." );
	};

	useEffect ( () => {
		if ( !props?.pageProps?.tokenExpired )
			return;

		resetUser ();
	}, [ props?.pageProps?.tokenExpired ] );

	useEffect ( () => {
		if ( !props?.pageProps?.auth )
			return;

		storeAuthDataClient ( props?.pageProps?.auth );
	}, [ props?.pageProps?.auth ] );

	const themeColorPalette = darkMode ? dark : light;

	useEffect ( () => {
		if ( darkMode )
			document.getElementsByTagName ( "html" )[0].style.backgroundColor = themeColorPalette?.background?.secondary;
		else
			document.getElementsByTagName ( "html" )[0].style.backgroundColor = themeColorPalette?.background2?.primary;

		document.body.style.backgroundColor = darkMode ? "#000000" : "#fff";

		const htmlEl    = document.querySelector ( "html" );
		const themeName = darkMode ? "dark" : "light";

		htmlEl.setAttribute ( "theme", themeName );
	}, [ darkMode ] );

	return (
		<>
			<Script src = "https://player.live-video.net/1.8.0/amazon-ivs-player.min.js" />

			<Script src = "https://securepubads.g.doubleclick.net/tag/js/gpt.js" />

			<Analytics />

			<Schema />

			<GoogleOAuthProvider clientId = { process.env.GOOGLE_SIGNIN_CLIENT_ID }>
				<CacheProvider value = { emotionCache }>
					<ThemeProvider theme = { { ...muiTheme } }>
						<ThemeContainer theme = { { ...muiTheme, ...cssVariables, ...themeLight, colors: themeColorPalette, typographyNew, darkMode: darkMode, paletteNew: palette } }>
							<CssBaseline />

							<ToastContainer
								hideProgressBar
								pauseOnFocusLoss
								pauseOnHover
								autoClose = { 3000 }
								closeButton = { false }
								closeOnClick = { false }
								newestOnTop = { false }
								position = "bottom-center"
								theme = "colored"
							/>

							<Layout>
								<RouterTransition />

								<CustomComp Component = { Component }
									{ ...props }
									{ ...pageProps }
								/>
							</Layout>
						</ThemeContainer>
					</ThemeProvider>
				</CacheProvider>
			</GoogleOAuthProvider>
		</>
	);
};

export default component;

function WrapperComp ( props ) {
	const { Component, ...otherProps } = props;

	return <Component { ...otherProps } />;
}

const CustomComp = withAuth ( WrapperComp );
